import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTitle } from '../../context/TitleContext';
import DataTable from '../../components/DataTable/DataTable';
import orderRepository from '../../services/repositories/order.repository';
import { getOrderTotals } from '../../utils/orderTotals';
import '../../styles/products.scss';

const OrderIndex = () => {
  const { setTitle } = useTitle();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [editingPaymentId, setEditingPaymentId] = useState(null);

  // Status options
  const statusOptions = [
    { value: 'pending', label: 'En attente', color: 'warning' },
    { value: 'processing', label: 'En traitement', color: 'info' },
    { value: 'shipped', label: 'Expédié', color: 'primary' },
    { value: 'delivered', label: 'Livré', color: 'success' },
    { value: 'cancelled', label: 'Annulé', color: 'danger' },
  ];

  // Payment options
  const paymentOptions = [
    { value: 'pending', label: 'En attente', color: 'warning' },
    { value: 'paid', label: 'Payé', color: 'success' },
    { value: 'failed', label: 'Échoué', color: 'danger' },
    { value: 'refunded', label: 'Remboursé', color: 'info' },
  ];

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await orderRepository.getAll();
      
      // Transform API data to match DataTable format
      const transformedOrders = Array.isArray(response) ? response : (response.data || []);
      
      const formattedOrders = transformedOrders.map(order => {
        const totals = getOrderTotals(order);
        return {
          id: order.id,
          order_number: order.order_number || order.serial_name || '-',
          total: totals.grandTotal,
          tax: totals.tax,
          subtotal: totals.subtotal,
          client_name: order.first_name && order.last_name 
            ? `${order.first_name} ${order.last_name}` 
            : (order.user?.name || '-'),
          status: order.status || 'pending',
          payment_status: order.payment_status || 'pending',
          city: order.city || '-',
          created_at: order.created_at || '-',
        };
      });
      
      setOrders(formattedOrders);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.message || 'Error loading orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setTitle('Liste des Commandes');
    fetchOrders();
  }, [setTitle, fetchOrders]);

  // Handle status update
  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await orderRepository.updateStatus(orderId, newStatus);
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
      setEditingStatusId(null);
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  // Handle payment status update
  const handleUpdatePayment = async (orderId, newPaymentStatus) => {
    try {
      await orderRepository.updatePaymentStatus(orderId, newPaymentStatus);
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, payment_status: newPaymentStatus } : order
      ));
      setEditingPaymentId(null);
    } catch (err) {
      console.error('Error updating payment status:', err);
      alert('Erreur lors de la mise à jour du paiement');
    }
  };

  // Define columns for DataTable
  const columns = useMemo(() => [
    {
      header: 'N° Commande',
      accessor: 'order_number',
      sortable: true,
      render: (value) => <span className="fw-semibold text-primary">{value}</span>,
    },
    {
      header: 'Client',
      accessor: 'client_name',
      sortable: true,
    },
    {
      header: 'Total',
      accessor: 'total',
      sortable: true,
      render: (value) => <span className="fw-semibold">{parseFloat(value).toFixed(2)} DH</span>,
    },
    {
      header: 'Ville',
      accessor: 'city',
      sortable: true,
    },
    {
      header: 'Statut',
      accessor: 'status',
      sortable: true,
      render: (value, row) => {
        const statusInfo = statusOptions.find(s => s.value === value) || { label: value, color: 'secondary' };
        
        if (editingStatusId === row.id) {
          return (
            <div className="dropdown show" style={{ display: 'inline-block' }}>
              <select
                className={`form-select form-select-sm bg-${statusInfo.color} text-white`}
                style={{ minWidth: '140px', cursor: 'pointer' }}
                value={value}
                onChange={(e) => handleUpdateStatus(row.id, e.target.value)}
                onBlur={() => setEditingStatusId(null)}
                autoFocus
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          );
        }
        
        return (
          <span 
            className={`badge bg-${statusInfo.color}`}
            style={{ cursor: 'pointer' }}
            onDoubleClick={() => setEditingStatusId(row.id)}
            title="Double-cliquez pour modifier"
          >
            {statusInfo.label}
          </span>
        );
      },
    },
    {
      header: 'Paiement',
      accessor: 'payment_status',
      sortable: true,
      render: (value, row) => {
        const paymentInfo = paymentOptions.find(p => p.value === value) || { label: value || 'En attente', color: 'warning' };
        
        if (editingPaymentId === row.id) {
          return (
            <div className="dropdown show" style={{ display: 'inline-block' }}>
              <select
                className={`form-select form-select-sm bg-${paymentInfo.color} text-white`}
                style={{ minWidth: '140px', cursor: 'pointer' }}
                value={value || 'pending'}
                onChange={(e) => handleUpdatePayment(row.id, e.target.value)}
                onBlur={() => setEditingPaymentId(null)}
                autoFocus
              >
                {paymentOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          );
        }
        
        return (
          <span 
            className={`badge bg-${paymentInfo.color}`}
            style={{ cursor: 'pointer' }}
            onDoubleClick={() => setEditingPaymentId(row.id)}
            title="Double-cliquez pour modifier"
          >
            {paymentInfo.label}
          </span>
        );
      },
    },
    {
      header: 'Date',
      accessor: 'created_at',
      sortable: true,
      render: (value) => {
        if (!value || value === '-') return '-';
        const date = new Date(value);
        return date.toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
      },
    },
  ], [editingStatusId, editingPaymentId, orders, statusOptions, paymentOptions]);

  const handleDelete = useCallback(async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) {
      try {
        await orderRepository.delete(id);
        fetchOrders();
      } catch (err) {
        console.error('Error deleting order:', err);
        alert('Erreur lors de la suppression de la commande');
      }
    }
  }, [fetchOrders]);

  const handleBulkDelete = useCallback(async (ids) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${ids.length} commande(s) ?`)) {
      try {
        await orderRepository.bulkDelete(ids);
        fetchOrders();
      } catch (err) {
        console.error('Error bulk deleting orders:', err);
        alert('Erreur lors de la suppression des commandes');
      }
    }
  }, [fetchOrders]);

  const actions = useMemo(() => [
    {
      label: 'Voir',
      icon: 'solar:eye-bold-duotone',
      onClick: (row) => navigate(`/admin/orders/order-show/${row.id}`),
      variant: 'info',
    },
    {
      label: 'Modifier',
      icon: 'solar:pen-bold-duotone',
      onClick: (row) => navigate(`/admin/orders/order-edit/${row.id}`),
      variant: 'warning',
    },
    {
      label: 'Supprimer',
      icon: 'solar:trash-bin-trash-bold-duotone',
      onClick: (row) => handleDelete(row.id),
      variant: 'danger',
    },
  ], [navigate, handleDelete]);

  if (loading && orders.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="order-index-page">
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      <DataTable
        data={orders}
        columns={columns}
        actions={actions}
        onBulkAction={handleBulkDelete}
        searchPlaceholder="Rechercher une commande..."
        addButtonLabel="Nouvelle Commande"
        onAddClick={() => navigate('/admin/orders/order-add')}
      />
    </div>
  );
};

export default OrderIndex;
